/**
 * useMutation hooks for the Story List write path — folder + story CRUD
 * Replaces the mutation effects in `storyListEffects` (compose-app).
 *
 * Auth (§1): GraphQL writes go through `postQuery` (cookie/session auth) and
 * REST writes through `transport`. Story List is non-admin reachable.
 *
 * Cache: each hook invalidates the broad key prefix it affects on success,
 * replacing the effects' explicit `getStoryFolders` / `getStories` re-dispatches.
 *   FOLDERS_KEY — `storyFolders` prefix: folder CRUD + move
 *   STORIES_KEY — `stories` prefix: delete/move stories
 *
 * Orchestration left to the caller:
 *   - navigation (navigateToFolderList, navigateToStoryEdit, etc.)
 *   - local selection state (setListSelection)
 *   - success toasts (caller has context like folder names / counts)
 *   - Hasura subscription wiring for copy jobs (copyStories / copyStoryFolder
 *     fire only the REST kick-off; the caller subscribes to progress updates)
 *
 * Toasts use `useToast` (§8); copy is passed in by the caller.
 *
 * createStory / createStoryFromTemplate: inline graphql +
 * storyViewEffects.createStoryContent — no longer route through storyListEffects.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SegmentEffects } from '@opengov/app-core/analytics';
import { transport } from '@opengov/app-core/transport';
import {
  STORIES_EDITOR_PRIVILEGE,
  STORIES_PDF_GENERATION_PRIVILEGE,
  STORIES_PUBLISHER_PRIVILEGE,
  STORIES_VIEWER_PRIVILEGE,
} from '@opengov/stories-shared/constants';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import { ISettings, ICreateStoryProps, ICreateStoryFromTemplateProps } from '../../../../types';
import { processStoryContentOrTemplateContent } from '@opengov/stories-shared/graphQL';
import {
  orderStoryContents,
  selectNewStoryContents,
  sortStoryContents,
} from '@opengov/stories-shared/utils';
import shortid from 'shortid';
import * as qs from 'query-string';

import { postQuery } from '../../../effects/publications/postQuery';
import { copyStoryContentHTML } from '../../../common/utils';
import {
  applyBrandingSettings,
  updateStoryContentId,
} from '../../../utils';
import {
  createStory as createStoryMutation,
  createStoryFolder,
  createStoryFolders,
  deleteStories,
  deleteStoriesAndFolderByFolderIds,
  getTemplateById,
  moveFolders,
  moveStories,
  moveStoriesByFolderId,
  organizeStoriesInFolder,
  organizeStoryFolders,
  renameStoryFolder,
  upsertStoryUser,
} from '../../../graphQL/queries/listQueries';
import {
  updateStoryContent as updateStoryContentQuery,
  updateTemplateContent,
} from '../../../graphQL/queries';
import {
  navigateToStoryEdit,
  navigateToPublicationStoryEditPath,
} from '../../../state/navigation';
import { insertEmbeds } from '../../../routes/hasura';
import { processStoryOrTemplate } from '../../../transformations/listTransformation';
import { useToast } from '../../Toast/context/useToast.context';
import {
  ICopyStoriesGraphqlRequest,
  ICopyStoryFolderGraphqlRequest,
  ICreateStoryFolderGraphqlRequest,
  ICreateStoryFoldersGraphqlRequest,
  IDeleteStoriesGraphqlResponse,
  IExportPdfProcess,
  IExportPdfRequest,
  IMoveFolderGraphqlRequest,
  IMoveStoriesByFolderIdGraphqlRequest,
  IMoveStoriesGraphqlRequest,
  IMoveStoriesGraphqlResponse,
  IOrganizeStoriesGraphQlRequest,
  IOrganizeStoryFoldersGraphQlRequest,
  IPreviewPdfFolderRequest,
  IRenameStoryFolderGraphqlRequest
} from '../../../../types/storyListTypes';
import { storyListQueryKeys } from './storyListQueryKeys';
import { publicationViewQueryKeys } from '../../Publications/PublicationView/hooks/publicationViewQueryKeys';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type ToastCopy = {
  title: string;
  body?: string;
};

// Broad invalidation keys — all param-variants under each prefix refetch.
const FOLDERS_KEY = [...storyListQueryKeys.all, 'storyFolders'];
const STORIES_KEY = [...storyListQueryKeys.all, 'stories'];

// ---------------------------------------------------------------------------
// useDeleteStories
// Replaces storyListEffects.deleteStories
// ---------------------------------------------------------------------------

export type DeleteStoriesInput = {
  storyIds: string[];
  publicationId: string;
  entityId: number;
};

export function useDeleteStories(errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<IDeleteStoriesGraphqlResponse, Error, DeleteStoriesInput>({
    mutationFn: async ({ storyIds, publicationId, entityId }) => {
      const res: IDeleteStoriesGraphqlResponse = await postQuery(deleteStories, { ids: storyIds });
      // Fire-and-forget thumbnail refresh (same as the retired effect).
      if (publicationId) {
        void transport
          .url(`/api/stories/v1/publications/${publicationId}/updateThumbnail`)
          .json({ publicationId, entityId })
          .post()
          .json();
      }
      return res;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORIES_KEY });
      // Deleting from inside a publication renders rows from
      // `publicationViewQueryKeys.stories` (a different query than STORIES_KEY) —
      // refresh the publication list + section tree so the row disappears.
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'stories'] });
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'foldersHierarchy'] });
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useMoveStories
// Replaces storyListEffects.moveStories (supports both move variants)
// ---------------------------------------------------------------------------

export type MoveStoriesInput = IMoveStoriesGraphqlRequest | IMoveStoriesByFolderIdGraphqlRequest;

const isMoveByFolderId = (
  vars: MoveStoriesInput
): vars is IMoveStoriesByFolderIdGraphqlRequest =>
  (vars as IMoveStoriesByFolderIdGraphqlRequest).targetFolderId !== undefined;

export function useMoveStories(errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<IMoveStoriesGraphqlResponse, Error, MoveStoriesInput>({
    mutationFn: async (input) => {
      const query = isMoveByFolderId(input) ? moveStoriesByFolderId : moveStories;
      return postQuery(query, input) as Promise<IMoveStoriesGraphqlResponse>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORIES_KEY });
      // Moving a story between publication sections changes the publication list —
      // refresh its story query + section tree too.
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'stories'] });
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'foldersHierarchy'] });
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useCopyStories
// Replaces storyListEffects.copyStories REST kick-off.
// Caller wires the Hasura subscription (storiesCopyProcessesSubscriptionQuery)
// to track progress — this hook only initiates the async job.
// ---------------------------------------------------------------------------

export type CopyStoriesInput = ICopyStoriesGraphqlRequest & {
  entityId: number;
  entitySubdomain: string;
};

export function useCopyStories(errorToast: ToastCopy) {
  const { toggleToast } = useToast();

  return useMutation<void, Error, CopyStoriesInput>({
    mutationFn: async ({ ids, folderId, entityId, entitySubdomain }) => {
      await transport
        .url(`/api/stories/v1/stories/copy`)
        .json({
          stories_ids: ids,
          entity_id: entityId,
          entity_subdomain: entitySubdomain,
          destination_folder_id: folderId
        })
        .post()
        .json();
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useCreateStoryFolder
// Replaces storyListEffects.createStoryFolder
// Caller handles navigation (navigateToFolderList), selection state and the
// success toast (its copy interpolates the new folder name).
// ---------------------------------------------------------------------------

export type CreateStoryFolderResult = {
  insert_storyFolders_one: { id: string; title: string };
};

export function useCreateStoryFolder(errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<CreateStoryFolderResult, Error, ICreateStoryFolderGraphqlRequest>({
    mutationFn: async (input) => {
      const response = await postQuery(createStoryFolder, input);
      if (!response?.insert_storyFolders_one?.id) {
        return Promise.reject(response?.errors ?? new Error('createStoryFolder: no id returned'));
      }
      return response as CreateStoryFolderResult;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FOLDERS_KEY });
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useCreateStoryFolders (bulk)
// Replaces storyListEffects.createStoryFolders
// ---------------------------------------------------------------------------

export function useCreateStoryFolders(errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<unknown, Error, ICreateStoryFoldersGraphqlRequest>({
    mutationFn: async (input) => postQuery(createStoryFolders, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FOLDERS_KEY });
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useCopyStoryFolder
// Replaces storyListEffects.copyStoryFolder REST kick-off.
// Caller wires the Hasura subscription (folderCopyProcessesSubscriptionQuery)
// to track progress — this hook only initiates the async job.
// ---------------------------------------------------------------------------

export type CopyStoryFolderInput = ICopyStoryFolderGraphqlRequest & {
  entityId: number;
  entitySubdomain: string;
};

export function useCopyStoryFolder(errorToast: ToastCopy) {
  const { toggleToast } = useToast();

  return useMutation<void, Error, CopyStoryFolderInput>({
    mutationFn: async ({ sourceFolderId, destinationFolderId, publicationId, entityId, entitySubdomain }) => {
      await transport
        .url(`/api/stories/v1/folders/${sourceFolderId}/copy`)
        .json({
          entity_id: entityId,
          entity_subdomain: entitySubdomain,
          destination_folder_id: destinationFolderId,
          publication_id: publicationId
        })
        .post()
        .json();
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useRenameStoryFolder
// Replaces storyListEffects.renameStoryFolder
// Caller handles selection state update (setListSelection).
// ---------------------------------------------------------------------------

export function useRenameStoryFolder(errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<void, Error, IRenameStoryFolderGraphqlRequest>({
    mutationFn: async ({ id, title }) => {
      await postQuery(renameStoryFolder, { id, title });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FOLDERS_KEY });
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useDeleteStoryFolders
// Replaces storyListEffects.deleteStoryFolders
// Two sequential writes: hard-delete stories + soft-rename the folder.
// ---------------------------------------------------------------------------

export type DeleteStoryFoldersInput = {
  folderIds: string[];
  folderName: string;
};

export function useDeleteStoryFolders(errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<void, Error, DeleteStoryFoldersInput>({
    mutationFn: async ({ folderIds, folderName }) => {
      await postQuery(deleteStoriesAndFolderByFolderIds, { folderIds });
      // Soft-rename to support audit / restore (same as the retired effect).
      await postQuery(renameStoryFolder, {
        id: folderIds[0],
        title: `${folderName}-deleted-${Date.now()}`
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FOLDERS_KEY });
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useMoveFolders
// Replaces storyListEffects.moveFolders
// ---------------------------------------------------------------------------

export function useMoveFolders(errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<void, Error, IMoveFolderGraphqlRequest>({
    mutationFn: async ({ ids, parentFolderId }) => {
      await postQuery(moveFolders, { ids, parentFolderId });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FOLDERS_KEY });
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useExportFolderPdf
// Replaces storyListEffects.exportFolderPdf
// Returns IExportPdfProcess so the caller can track status via subscription.
// ---------------------------------------------------------------------------

export type ExportFolderPdfInput = IExportPdfRequest & { entityId: number };

export function useExportFolderPdf(errorToast: ToastCopy) {
  const { toggleToast } = useToast();

  return useMutation<IExportPdfProcess, Error, ExportFolderPdfInput>({
    mutationFn: async ({ folderId, pdfOptions, entityId }) => {
      const response = await transport
        .url(`/api/stories/v1/folders/${folderId}/export-pdf`)
        .json({ pdf_options: pdfOptions, entity_id: entityId })
        .post()
        .json();
      return response as IExportPdfProcess;
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// usePreviewFolderPdf
// Replaces storyListEffects.getPdfPreviewLink (GET with AbortController).
// Returns the PDF URL string, or '' on abort/error.
// ---------------------------------------------------------------------------

export type PreviewFolderPdfInput = IPreviewPdfFolderRequest & {
  entityId: number;
  entitySubdomain: string;
};

export function usePreviewFolderPdf(errorToast: ToastCopy) {
  const { toggleToast } = useToast();

  return useMutation<string, Error, PreviewFolderPdfInput>({
    mutationFn: async ({ folderId, pdfOptions, entityId, entitySubdomain, controller }) => {
      const queryParams = qs.stringify(
        {
          ...pdfOptions,
          entityId,
          entitySubdomain,
          margins: encodeURIComponent(JSON.stringify(pdfOptions.margins))
        },
        { encode: false }
      );
      let req = transport.url(`/api/stories/v1/folders/${folderId}/preview-pdf?${queryParams}`);
      if (controller) {
        // wretch's `.signal()` takes the AbortController itself and reads
        // `.signal` off it internally — passing the signal disables aborting.
        req = req.signal(controller);
      }
      const response = await req
        .get()
        .json()
        .catch((error: { name?: string }) => {
          // Aborts are expected (a newer preview is already in flight) — resolve
          // to '' so the mutation does not flip to an error state. Real failures
          // propagate so the modal can show the preview-failure message.
          if (error?.name === 'AbortError') return { pdf: '' };
          throw error;
        });
      return (response.pdf ?? '') as string;
    },
    onError: (err) => {
      // Suppress AbortError — new preview is in flight (user changed form values).
      if (err?.name !== 'AbortError') {
        toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
      }
    }
  });
}

// ---------------------------------------------------------------------------
// useOrganizeStoriesInFolder
// Replaces storyListEffects.organizeStoriesById.
// Shows success toast on completion; invalidates stories key so the list
// re-fetches with the new custom order automatically.
// ---------------------------------------------------------------------------

export type OrganizeSuccessToast = { title: string; body: string };

export function useOrganizeStoriesInFolder(
  errorToast: ToastCopy,
  successToast?: OrganizeSuccessToast
) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<void, Error, IOrganizeStoriesGraphQlRequest>({
    mutationFn: async ({ orderedStoriesIds }) => {
      await postQuery(organizeStoriesInFolder(orderedStoriesIds), {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORIES_KEY });
      void queryClient.invalidateQueries({ queryKey: FOLDERS_KEY });
      if (successToast) {
        toggleToast({ title: successToast.title, body: successToast.body, severity: 'success' });
      }
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// useOrganizeStoryFolders
// Replaces storyListEffects.organizeStoryFoldersById.
// Shows success toast on completion; invalidates folders key.
// ---------------------------------------------------------------------------

export function useOrganizeStoryFolders(
  errorToast: ToastCopy,
  successToast?: OrganizeSuccessToast
) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<void, Error, IOrganizeStoryFoldersGraphQlRequest>({
    mutationFn: async ({ orderedFoldersIds }) => {
      await postQuery(organizeStoryFolders(orderedFoldersIds), {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FOLDERS_KEY });
      if (successToast) {
        toggleToast({ title: successToast.title, body: successToast.body, severity: 'success' });
      }
    },
    onError: () => {
      toggleToast({ title: errorToast.title, body: errorToast.body, severity: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// createStoryAndContents — shared helper
// Replaces the private `postQueryCreateStory` inside storyListEffects.
// Two-step: upsert user → insert story → create each content block.
// ---------------------------------------------------------------------------

async function upsertUserAndCreateStory(
  userObj: object,
  storyObj: object
): Promise<IStory> {
  const userRes = await transport
    .url(`/api/data/objects/stories/v1/graphql`)
    .json({ query: upsertStoryUser, variables: { user: userObj } })
    .post()
    .json();
  if (!userRes.data) return Promise.reject(userRes.errors);
  const authorId: string = userRes.data.insert_storyUsers_one._id;

  const storyRes = await transport
    .url(`/api/data/objects/stories/v1/graphql`)
    .json({
      query: createStoryMutation,
      variables: { story: { ...storyObj, createdBy: authorId, authorId, updatedBy: authorId } },
    })
    .post()
    .json();
  if (!storyRes.data) return Promise.reject(storyRes.errors);
  return processStoryOrTemplate(storyRes.data.insert_stories_one);
}

// ---------------------------------------------------------------------------
// createStoryContentDirect
// Inlined from storyViewEffects.createStoryContent. Two-step:
//   1. POST to create the content record (gets server-assigned _id).
//   2. Apply branding + embed _id in HTML, then POST update.
// Skips Redux dispatches from the original effect.
// ---------------------------------------------------------------------------

export async function createStoryContentDirect(e: {
  isTemplate: boolean;
  entityId: number;
  story: IStory;
  userId?: string;
  createFromTemplate: boolean;
  storyContent: IStoryContent;
  order?: number;
  settings?: ISettings;
  entityName?: string;
}): Promise<void> {
  const { isTemplate, entityId, story, userId, createFromTemplate, storyContent, order, settings, entityName } = e;

  const storyContentData: any = {
    [isTemplate ? 'storyTemplateId' : 'storyId']: story._id,
    title: storyContent.title,
    description: storyContent.description,
    content: storyContent.content,
    contentHtml: storyContent.content,
    group: storyContent.group,
    format: storyContent.format,
    type: storyContent.type,
    order: order || storyContent.order,
    active: true,
    authorId: userId,
    entityId,
  };
  const createRes = await transport
    .url(`/api/data/objects/stories/v1/graphql`)
    .json({ query: isTemplate ? updateTemplateContent : updateStoryContentQuery, variables: { storyContentData } })
    .post()
    .json();
  if (!createRes.data) return Promise.reject(createRes);

  let created = processStoryContentOrTemplateContent(createRes.data.contents);
  if (!isTemplate && !createFromTemplate && settings) {
    created = applyBrandingSettings(created, settings, entityName);
  }
  created = updateStoryContentId(created);

  const updateData: any = {
    _id: created._id,
    [isTemplate ? 'storyTemplateId' : 'storyId']: story._id,
    title: created.title,
    description: created.description,
    content: created.content,
    contentHtml: created.content,
    group: created.group,
    format: created.format,
    type: created.type,
    order: created.order,
    active: created.active,
    entityId: created.entity_id,
    authorId: created.author._id,
  };
  const updateRes = await transport
    .url(`/api/data/objects/stories/v1/graphql`)
    .json({ query: isTemplate ? updateTemplateContent : updateStoryContentQuery, variables: { storyContentData: updateData } })
    .post()
    .json();
  if (!updateRes.data) return Promise.reject(updateRes);
}

// ---------------------------------------------------------------------------
// useCreateStory
// Replaces storyListEffects.createStory. Uses createStoryContentDirect
// (inlined from storyViewEffects.createStoryContent).
// ---------------------------------------------------------------------------

export function useCreateStory() {
  const { toggleToast } = useToast();

  return useMutation<IStory, Error, ICreateStoryProps>({
    mutationFn: async (input) => {
      const {
        isAdmin,
        isTemplate,
        createFromTemplate = false,
        storyContents,
        entityId,
        entityName,
        entitySubdomain,
        settings,
        user,
        title,
        description,
        folderId,
        paddingTop,
        paddingBottom,
        customCssCode,
        publication,
      } = input;

      if (!isAdmin) {
        toggleToast({ title: 'Unauthorized', body: 'Could not create Story', severity: 'error' });
        throw new Error('Unauthorized');
      }

      const userObj = {
        firstName: user.first_name,
        lastName: user.last_name,
        opengovId: user.id,
        email: user.email,
        title: user.title,
        entityId,
      };
      const storyBase: any = {
        title: title || 'New Story',
        description,
        state: 'draft',
        entityId,
        storyKey: `${entitySubdomain}/${shortid.generate()}`,
        folderId,
        paddingBottom,
        paddingTop,
        customCssCode,
      };
      if (publication?.id) {
        storyBase.subjectStoryClaims = {
          data: [
            {
              subjectId: user.uuid,
              subjectType: 'user',
              storyClaims: [
                STORIES_VIEWER_PRIVILEGE,
                STORIES_EDITOR_PRIVILEGE,
                STORIES_PDF_GENERATION_PRIVILEGE,
                STORIES_PUBLISHER_PRIVILEGE,
              ],
            },
          ],
        };
      }

      const newStory = await upsertUserAndCreateStory(userObj, storyBase).catch((error) => {
        toggleToast({ title: 'Request Error', body: 'Could not create Story', severity: 'error' });
        throw error;
      });

      if (createFromTemplate) {
        await Promise.all(
          (storyContents ?? []).map((storyContent: IStoryContent) =>
            createStoryContentDirect({
              isTemplate,
              createFromTemplate,
              entityId,
              userId: newStory.author._id,
              story: newStory,
              storyContent,
            })
          )
        );
      } else {
        const defaultContents = selectNewStoryContents();
        const header = defaultContents.find((sc) => sc.format === 'header-1');
        const footer = defaultContents.find((sc) => sc.format === 'footer-1');
        await Promise.all([
          createStoryContentDirect({
            isTemplate,
            createFromTemplate: false,
            entityId,
            entityName,
            userId: newStory.author._id,
            story: newStory,
            storyContent: header as unknown as IStoryContent,
            settings,
          }),
          createStoryContentDirect({
            isTemplate,
            createFromTemplate: false,
            entityId,
            entityName,
            userId: newStory.author._id,
            story: newStory,
            storyContent: footer as unknown as IStoryContent,
            settings,
          }),
        ]);
        SegmentEffects.track({ eventName: 'Stories: Story: Created', props: newStory }).catch(() => null);
        if (publication?.id) {
          navigateToPublicationStoryEditPath(entitySubdomain, newStory._id, false, publication.id);
        } else {
          navigateToStoryEdit(entitySubdomain, newStory._id, false);
        }
      }

      return newStory;
    },
  });
}

// ---------------------------------------------------------------------------
// useCreateStoryFromTemplate
// Replaces storyListEffects.createStoryFromTemplate. Fetches template, then
// delegates to the inlined createStory logic (no compose-app effect chain).
// ---------------------------------------------------------------------------

export function useCreateStoryFromTemplate() {
  const { toggleToast } = useToast();

  return useMutation<IStory, Error, ICreateStoryFromTemplateProps>({
    mutationFn: async (input) => {
      const {
        isAdmin,
        entityId,
        entitySubdomain,
        folderId,
        templateId,
        user,
        title,
        description,
        paddingTop,
        paddingBottom,
        customCssCode,
        publication,
      } = input;

      if (!isAdmin) {
        toggleToast({ title: 'Unauthorized', body: 'Could not create Story', severity: 'error' });
        throw new Error('Unauthorized');
      }

      const templateRes = await transport
        .url(`/api/data/objects/stories/v1/graphql`)
        .json({ query: getTemplateById, variables: { id: templateId } })
        .post()
        .json()
        .catch((error: Error) => {
          toggleToast({ title: 'Request Error', body: 'Could not get Template', severity: 'error' });
          throw error;
        });
      if (!templateRes.data) {
        toggleToast({ title: 'Request Error', body: 'Could not get Template', severity: 'error' });
        throw new Error('Template not found');
      }

      const { templateContents } = templateRes.data.resource;
      const processedContents: IStoryContent[] = (templateContents as any[]).map(
        processStoryContentOrTemplateContent
      );
      const sortedContents = orderStoryContents(sortStoryContents(processedContents));
      const embedIds: string[] = [];
      const updatedContents = sortedContents.map((sc) => {
        const match = sc?.contentHtml?.match(/data-embed-id="(.*?)"/);
        if (match) embedIds.push(match[1]);
        return { ...sc, content: copyStoryContentHTML(sc) };
      });

      const userObj = {
        firstName: user.first_name,
        lastName: user.last_name,
        opengovId: user.id,
        email: user.email,
        title: user.title,
        entityId,
      };
      const storyBase: any = {
        title: title || 'New Story',
        description,
        state: 'draft',
        entityId,
        storyKey: `${entitySubdomain}/${shortid.generate()}`,
        folderId,
        paddingBottom,
        paddingTop,
        customCssCode,
      };

      const newStory = await upsertUserAndCreateStory(userObj, storyBase);

      await Promise.all(
        updatedContents.map((sc) =>
          createStoryContentDirect({
            isTemplate: false,
            createFromTemplate: true,
            entityId,
            userId: newStory.author._id,
            story: newStory,
            storyContent: sc,
          })
        )
      );

      if (embedIds.length > 0) {
        void insertEmbeds(embedIds.map((embedId) => ({ storyId: newStory._id, embedId })));
      }

      if (publication?.id) {
        navigateToPublicationStoryEditPath(entitySubdomain, newStory._id, false, publication.id);
      } else {
        navigateToStoryEdit(entitySubdomain, newStory._id);
      }
      return newStory;
    },
  });
}
